import React from "react";
import Col from "../col";
import {
  WizardBaseProps,
  WizardSimpleStepProps,
  WizardStepProps,
  WizardStepStatus,
} from "./types";
import WizardProgress from "./wizardProgress";
import WizardSimpleStep from "./wizardSimpleStep";
import WizardSimpleStepWrapper from "./wizardSimpleStepWrapper";
import WizardStep from "./wizardStep";
import WizardStepWrapper from "./wizardStepWrapper";
import { useTranslation } from "react-i18next";

function getStatus(current: number, step: number): WizardStepStatus {
  if (step === current) {
    return "active";
  } else if (step < current) {
    return "completed";
  } else {
    return "pending";
  }
}

export default function WizardBase(props: Readonly<WizardBaseProps>) {
  const { currentStep, children, gap = "lg", ...rest } = props;

  const { t } = useTranslation();

  const steps = React.Children.toArray(children)
    .filter((child) => React.isValidElement(child))
    .filter((child) => child.type === WizardStep)
    .map((child) => child.props as WizardStepProps);

  const simpleSteps = React.Children.toArray(children)
    .filter((child) => React.isValidElement(child))
    .filter((child) => child.type === WizardSimpleStep)
    .map((child) => child.props as WizardSimpleStepProps);

  if (steps.length && simpleSteps.length) {
    throw new Error("Please provide steps or simple steps only");
  }

  const progress = React.Children.toArray(children)
    .filter((child) => React.isValidElement(child))
    .find((child) => child.type === WizardProgress);

  const stepCount = Math.max(steps.length, simpleSteps.length);
  const ratio = Math.round(100 / stepCount);

  return (
    <Col {...rest} gap={gap}>
      {steps.map((step, i) => (
        <WizardStepWrapper
          {...step}
          isFirst={i === 0}
          isLast={i === steps.length - 1}
          step={i + 1}
          status={getStatus(currentStep, i + 1)}
        />
      ))}
      {simpleSteps.map((step, i) => (
        <WizardSimpleStepWrapper
          {...step}
          isFirst={i === 0}
          isLast={i === steps.length - 1}
          step={i + 1}
          status={getStatus(currentStep, i + 1)}
        />
      ))}
      {!!progress && (
        <Col gap="sm" className="mt-2">
          <div className="h-2 w-full bg-gunmetal-100 rounded-full">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{
                width: `${(currentStep - 1) * ratio}%`,
              }}
            />
          </div>
          <div>
            {t("common.template.wizard_progress", "{{progress}}% Complete", {
              progress: Math.round((currentStep - 1) * ratio),
            })}
          </div>
        </Col>
      )}
    </Col>
  );
}
